<template>
  <div v-if="hasFlowContainer" ref="flowEl" class="flow" :class="classList">
    <transition-group
      ref="transitionGroup"
      :tag="ejected ? 'div' : undefined"
      :appear="appear"
      :css="false"
      @enter="onEnterHook"
      @before-enter="onBeforeEnterHook"
      @before-leave="onBeforeLeaveHook"
      @leave="onLeaveHook"
    >
      <slot />
    </transition-group>

    <slot name="footer" />
  </div>

  <transition-group
    v-else
    ref="transitionGroup"
    :tag="'div'"
    :appear="appear"
    :class="transitionGroupClassName"
    :css="false"
    @enter="onEnterHook"
    @before-enter="onBeforeEnterHook"
    @before-leave="onBeforeLeaveHook"
    @leave="onLeaveHook"
  >
    <slot />
  </transition-group>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, getCurrentInstance, onMounted, VNode } from 'vue';
import { FlowAnimation } from '@/components/Flow/Types';
import { collectFunctionsFromEl, recursivelyWaitForPromises } from '@/utilities/virtualDom';
import { isSsr, waitForImages } from '@/utilities/Utilities';
import { useStore } from '@/store';
import { onResize } from '@/services/iframe-embed';

export default defineComponent({
  name: 'Flow',
  components: {},
  inheritAttrs: false,
  props: {
    transitionGroupClassName: {
      type: String,
      required: false
    },
    ejected: {
      type: Boolean,
      default: true
    },
    hasFlowContainer: {
      type: Boolean,
      default: true
    },
    animationEnter: {
      type: Object as PropType<FlowAnimation>
    },
    animationLeave: {
      type: Object as PropType<FlowAnimation>
    }
  },
  emits: ['left'],
  setup(props, context) {
    const flowEl = ref<HTMLElement | null>(null);
    const transitioningEnter = ref(false);
    const transitioningLeave = ref(false);
    const currentInstance = getCurrentInstance();
    const campaignState = useStore().getters['campaign/state'];

    const appear = ref(!isSsr());

    // eslint-disable-next-line
    const transitionGroup = ref<any>();

    let allowAnimateHeight = false;
    let previouslyEnteredVNode: VNode | undefined;
    let leaveAnimation: FlowAnimation | undefined;

    let readyPromiseResolve: (() => void) | undefined;

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const getCurrentVNode = (): VNode | undefined => {
      // eslint-disable-next-line
      // @ts-ignore-next-line
      if (currentInstance.vnode.component.subTree.children.default) {
        // eslint-disable-next-line
        // @ts-ignore-next-line
        return currentInstance.vnode.component.subTree.children.default()[0].children[0]
          .children[0]; /* eslint-disable-line */
      }

      // eslint-disable-next-line
      // @ts-ignore-next-line
      return currentInstance.vnode.component.subTree.children[0].component.subTree
        .children[0]; /* eslint-disable-line */
    };

    const classList = computed(() => {
      return {
        // Handles margin issues for flow pages.
        'flow--not-ejected': !props.ejected,
        'flow--ejected': props.ejected,
        // Handles transition CSS property for animating the height.
        'flow--transitioning': transitioningEnter.value || transitioningLeave.value
      };
    });

    const onBeforeLeaveHook = () => {
      transitioningLeave.value = true;
    };

    const onLeaveHook = async (el: Element, done: () => void) => {
      if (el.classList.contains('positioner') && el.childNodes[0] && el.childNodes[0] instanceof HTMLElement) {
        el = el.childNodes[0];
      }

      if (!previouslyEnteredVNode) {
        return;
      }

      const useLeaveAnimation = leaveAnimation;
      const leaveNode = previouslyEnteredVNode;

      await recursivelyWaitForPromises(leaveNode, 'onBeforeLeave', 1);

      if (useLeaveAnimation) {
        const animateEnd = () => {
          collectFunctionsFromEl(leaveNode, 'onAfterLeave').map((func) => func());

          context.emit('left');

          done();
          transitioningLeave.value = false;
          el.removeEventListener('animationend', animateEnd);
        };

        if (el instanceof HTMLElement) {
          el.style.cssText += `
            width: ${el.offsetWidth}px;
            -webkit-animation-duration: ${useLeaveAnimation.duration}ms;
            -moz-animation-duration: ${useLeaveAnimation.duration}ms;
            animation-duration: ${useLeaveAnimation.duration}ms;
          `;
        }

        el.classList.add('flow__page--leave');

        setTimeout(async () => {
          collectFunctionsFromEl(leaveNode, 'onBeforeLeaveAnimation').forEach((func) => func());

          if (useLeaveAnimation && el instanceof HTMLElement) {
            el.addEventListener('animationend', animateEnd);
            el.classList.add('animated', 'ng-leave', useLeaveAnimation.name);

            el.offsetWidth;

            if (window.getComputedStyle(el).animationName === 'none') {
              setTimeout(animateEnd, useLeaveAnimation.duration);
            }
          }
        }, useLeaveAnimation.delay);
      } else {
        collectFunctionsFromEl(leaveNode, 'onBeforeLeaveAnimation').forEach((func) => func());
        collectFunctionsFromEl(leaveNode, 'onAfterLeave').map((func) => func());

        context.emit('left');

        done();
      }
    };

    const onBeforeEnterHook = (el: Element) => {
      if (el.classList.contains('positioner') && el.childNodes[0] && el.childNodes[0] instanceof HTMLElement) {
        el = el.childNodes[0];
      }

      previouslyEnteredVNode = getCurrentVNode();

      if (el instanceof HTMLElement) {
        el.style.opacity = '0';
      }

      if (allowAnimateHeight && flowEl.value && !campaignState.isPopup) {
        let height = flowEl.value.offsetHeight;
        let marginTop = 0;
        if (el instanceof HTMLElement) {
          marginTop = Number(el.style.marginTop.replace('px', ''));
        }
        if (marginTop) {
          height = flowEl.value.offsetHeight + marginTop;
        }
        flowEl.value.style.height = `${height}px`;
      }

      onResize();

      transitioningEnter.value = true;
    };

    const onEnterHook = async (el: Element, done: () => void) => {
      if (el.classList.contains('positioner') && el.childNodes[0] && el.childNodes[0] instanceof HTMLElement) {
        el = el.childNodes[0];
      }

      const currentInstance = getCurrentVNode();
      previouslyEnteredVNode = currentInstance;

      if (!currentInstance) {
        return;
      }

      let animation: FlowAnimation | undefined = undefined;
      const animations = collectFunctionsFromEl(transitionGroup.value._.vnode, 'enterAnimation');

      if (animations.length > 0) {
        animation = animations[0]() as FlowAnimation | undefined;
      }

      const leaveAnimations = collectFunctionsFromEl(transitionGroup.value._.vnode, 'leaveAnimation');

      if (leaveAnimations.length > 0) {
        leaveAnimation = leaveAnimations[0]() as FlowAnimation | undefined;
      } else {
        leaveAnimation = undefined;
      }

      if (!leaveAnimation && props.animationLeave) {
        leaveAnimation = props.animationLeave;
      }

      await recursivelyWaitForPromises(currentInstance, 'onBeforeEnter', 3);

      if (el instanceof HTMLElement) {
        await waitForImages(el);
      }

      if (animation && animation?.name) {
        let handledAnimateEnd = false;

        const animateEnd = () => {
          // A safe-guard to ensure that we don't call animateEnd multiple
          // times. This is because we have a fallback to a setTimeout()
          // when animation isn't playing.
          if (handledAnimateEnd) {
            return;
          }

          handledAnimateEnd = true;

          el.removeEventListener('animationend', animateEnd);
          transitioningEnter.value = false;

          if (el instanceof HTMLElement) {
            el.style.cssText += 'opacity:1';
          }

          if (readyPromiseResolve) {
            readyPromiseResolve();
          }

          collectFunctionsFromEl(currentInstance, 'onAfterEnter').forEach((func) => func());

          /**
           * Remove height and classes after animation is done playing
           * The reason for this is that we saw that sometimes it removed height and class name to early, making
           * the animations not work properly
           */
          setTimeout(() => {
            if (flowEl.value && !campaignState.isPopup) {
              flowEl.value.style.height = '';
              onResize();
            }

            if (animation) {
              el.classList.remove('animated', animation.name);
            }
          }, animation?.duration || 0);

          done();
        };

        if (el instanceof HTMLElement) {
          if (allowAnimateHeight && flowEl.value && !campaignState.isPopup) {
            /**
             * When an element is comming into the view - with no height - we assume its because it has display: none / not supposed to be rendered
             * therefor we can also assume the height should not be applied.
             */
            if (el.offsetHeight !== 0) {
              onResize();

              let height = el.offsetHeight;
              let marginTop = 0;
              if (el instanceof HTMLElement) {
                marginTop = Number(el.style.marginTop.replace('px', ''));
              }
              flowEl.value.style.height = `${height + marginTop}px`;
            }
          }

          el.style.cssText += `
            -webkit-animation-duration: ${animation.duration}ms;
            -moz-animation-duration: ${animation.duration}ms;
            animation-duration: ${animation.duration}ms;
          `;
        }

        allowAnimateHeight = true;

        setTimeout(() => {
          collectFunctionsFromEl(currentInstance, 'onBeforeEnterAnimation').forEach((func) => func());

          if (animation && el instanceof HTMLElement) {
            el.addEventListener('animationend', animateEnd);
            el.classList.add('animated', animation.name);
            el.style.cssText += 'opacity:1;';

            el.offsetWidth;

            if (
              // The below offsetParent check will ensure that if customer has custom CSS that
              // hides one or more of the parent nodes, and doesn't play the animation,
              // then it falls back to setTimeout().
              el.offsetParent === null ||
              window.getComputedStyle(el).animationName === 'none'
            ) {
              setTimeout(animateEnd, animation.duration);
            }
          }
        }, animation.delay);
      } else {
        collectFunctionsFromEl(currentInstance, 'onBeforeEnterAnimation').forEach((func) => func());

        allowAnimateHeight = true;
        transitioningEnter.value = false;

        if (el instanceof HTMLElement) {
          el.style.cssText += 'opacity:1';
        }

        if (flowEl.value && !campaignState.isPopup) {
          flowEl.value.style.height = '';
        }

        if (readyPromiseResolve) {
          readyPromiseResolve();
        }

        if (el instanceof HTMLElement) {
          el.style.opacity = '1';
        }

        collectFunctionsFromEl(currentInstance, 'onAfterEnter').forEach((func) => {
          func();
        });

        done();
      }
    };

    onMounted(() => {
      previouslyEnteredVNode = getCurrentVNode();
    });

    return {
      onBeforeEnterHook,
      onEnterHook,
      onBeforeLeaveHook,
      onLeaveHook,
      flowEl,
      classList,
      transitionGroup,
      appear,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>
